<script setup lang="ts">
import { computed, ref } from 'vue'
import { EbomBulkAllocateStepNode } from '@/components/ebom/EbomBulkAllocateStepTree.vue'
import useIdSelection from '@/composables/useIdSelection'
import { useMutation } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import EbomBulkAllocateStepTreeNode from '@/components/ebom/EbomBulkAllocateStepTreeNode.vue'
import {
  EbomBulkAllocateStepTreeNodeAllocateMutation,
  EbomBulkAllocateStepTreeNodeAllocateMutationVariables,
} from '@/generated/graphql'
import Cross3dModelWarningDialog from '@/components/ebom/Cross3dModelWarningDialog.vue'
import { checkCross3dModelConflict } from '@/composables/useCross3dModelCheck'
import { useSetStepPreferred3dModel } from '@/composables/useSetStepPreferred3dModel'
import { ComponentExposed } from 'vue-component-type-helpers'

const props = defineProps<{
  node: EbomBulkAllocateStepNode
  bopId: string
  ebomId: string
}>()

const { selectedIds, resetSelection } = useIdSelection('ebom-bulk-allocation-parts')

const isOpen = ref(false)
const hasChildren = computed(() => props.node.children.length > 0)
const step = computed(() => props.node.step)

const dropContainer = ref<HTMLElement>()
const variant = ref<'text' | 'tonal'>('text')
const dragEnter = () => {
  variant.value = 'tonal'
}
const dragLeave = () => {
  variant.value = 'text'
}

const mutation = useMutation<
  EbomBulkAllocateStepTreeNodeAllocateMutation,
  EbomBulkAllocateStepTreeNodeAllocateMutationVariables
>(
  gql`
    mutation EbomBulkAllocateStepTreeNodeAllocate($command: BulkAllocateEbomPartsToStepCommand!) {
      article {
        bulkAllocateEbomPartsToStep(command: $command)
      }
    }
  `,
  {
    refetchQueries: ['EbomBulkAllocatePartTreeData', 'EbomPartTreeEbomParts', 'BopStepViewerData'],
  },
)
const setStepPreferred3dModel = useSetStepPreferred3dModel()
const warningDialog = ref<ComponentExposed<typeof Cross3dModelWarningDialog>>()

const allocateSelection = async () => {
  try {
    let chosenBopUploadId: string | undefined
    const check = await checkCross3dModelConflict({
      stepId: step.value.id,
      bopId: props.bopId,
      newEbomIds: [props.ebomId],
    })
    if (check.conflict) {
      chosenBopUploadId = await warningDialog.value?.open({
        currentEbomName: check.currentEbomName,
        newEbomName: check.newEbomName,
        currentBopUploadId: check.currentBopUploadId,
        newBopUploadId: check.newBopUploadId,
      })
      if (!chosenBopUploadId) return
    }
    await mutation.mutate({
      command: {
        stepId: step.value.id,
        ebomPartIds: selectedIds.value,
      },
    })
    if (chosenBopUploadId) {
      await setStepPreferred3dModel(step.value.id, chosenBopUploadId)
    }
    resetSelection()
  } finally {
    variant.value = 'text'
  }
}
</script>

<template>
  <v-list-item
    v-bind="$attrs"
    :variant="variant"
    density="compact"
    rounded
    style="user-select: none"
  >
    <template #prepend>
      <v-icon
        density="compact"
        :icon="hasChildren ? (isOpen ? 'folder_open' : 'folder') : 'handyman'"
        @click.stop="isOpen = !isOpen"
      />
    </template>
    <v-list-item-title>
      <div
        ref="dropContainer"
        class="dropContainer"
        @drop.prevent="allocateSelection"
        @dragover.prevent
        @dragenter.prevent="dragEnter"
        @dragleave.prevent="dragLeave"
      />
      {{ step.name }}
    </v-list-item-title>
    <template #append>
      <v-progress-circular v-if="mutation.loading.value" indeterminate />
    </template>
  </v-list-item>

  <div v-if="hasChildren && isOpen" class="children">
    <ebom-bulk-allocate-step-tree-node
      v-for="n in props.node.children"
      :key="n.id"
      :node="n"
      :bop-id="props.bopId"
      :ebom-id="props.ebomId"
    />
  </div>

  <cross3d-model-warning-dialog ref="warningDialog" />
</template>

<style scoped lang="scss">
.children {
  margin-left: 1.5em;
}
.dropContainer {
  position: absolute;
  width: 70%;
  height: 100%;
  z-index: 100;
}
</style>
